import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { TranscriptionJob } from '../types';
import { Results } from '../components/Results';
import { ArrowLeft, Calendar, Clock, FileText, Hourglass } from 'lucide-react';
import toast from 'react-hot-toast';

export function TranscriptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transcription, setTranscription] = useState<TranscriptionJob | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTranscription();
  }, [id]);

  const fetchTranscription = async () => {
    try {
      const { data, error } = await supabase
        .from('transcription_jobs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setTranscription(data);
    } catch (error) {
      console.error('Error fetching transcription:', error);
      toast.error('Failed to load transcription');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading transcription...</p>
        </div>
      </div>
    );
  }

  if (!transcription) return null;

  // Format duration from seconds to minutes and seconds
  const formatDuration = (seconds: number | undefined) => {
    if (!seconds && seconds !== 0) return 'Unknown duration';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    if (minutes === 0) {
      return `${remainingSeconds} seconds`;
    }
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>

          <div className="bg-card rounded-xl shadow-sm p-6 border">
            <h1 className="text-2xl font-bold text-foreground mb-4">{transcription.title}</h1>
            
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Calendar className="w-5 h-5" />
                <span className="text-sm">
                  {new Date(transcription.created_at).toLocaleDateString()}
                </span>
              </div>
              
              <div className="flex items-center gap-3 text-muted-foreground">
                <Clock className="w-5 h-5" />
                <span className="text-sm">
                  {new Date(transcription.created_at).toLocaleTimeString()}
                </span>
              </div>
              
              <div className="flex items-center gap-3 text-muted-foreground">
                <FileText className="w-5 h-5" />
                <span className="text-sm">
                  {transcription.video_metadata.filename || 'Unknown file'}
                </span>
              </div>

              <div className="flex items-center gap-3 text-muted-foreground">
                <Hourglass className="w-5 h-5" />
                <span className="text-sm">
                  {formatDuration(transcription.duration_seconds)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <Results
          transcript={transcription.transcript}
          caption={transcription.caption}
          contentGaps={transcription.content_gaps}
        />
      </div>
    </div>
  );
}